import { LEVEL_CONFIG } from '../../../config/level.config';
import { WORLD_CONFIG } from '../../../config/world.config';
import { ObstacleType } from '../../../obstacles/ObstacleType';
import {
  COLLECTIBLE_FLOAT_HEIGHT,
  collectibleLine,
  entryConnector,
  jumpArc,
  laneArea,
  laneCenterX,
  moduleSides,
  obstacleRow,
  standardLanes,
  straightExit,
} from '../segmentBuilders';
import { SegmentKind, type CollectibleSpawnPoint, type GroundSurface, type SegmentDefinition } from '../SegmentDefinition';

/**
 * SEG_05 – large construction pit 40 m (design/01_segments/SEG_05_Pit_Large).
 *
 *   z 12–24   pit over all three lanes, 3.5 m deep – too long for one jump, so every lane offers a route:
 *             lane 0  concrete block platform z 16.5–19.5 (two 4.5 m jumps)
 *             lane 1  steel beam bridge straight across (risk route with bottles – changing lanes on it means a fall)
 *             lane 2  two block platforms z 15.5–17 and 19.5–21 (three short jumps in a row)
 *   z 32      obstacle row behind the pit
 *
 * Every running line meets gaps of at most 4.5 m (single-jump limit 5 m at start speed).
 * The Blender module (tools/blender/segments/SEG_05_Pit_Large.py) is built from these numbers.
 */
export const PIT_LARGE = {
  length: 40,
  pit: { zFrom: 12, zTo: 24, depth: 3.5 },
  blockLeft: { lane: 0, zFrom: 16.5, zTo: 19.5 },
  blocksRight: { lane: 2, spans: [{ zFrom: 15.5, zTo: 17 }, { zFrom: 19.5, zTo: 21 }] },
  /** Two HEB beams side by side, exactly as wide as the runner's footprint. */
  beam: { lane: 1, halfWidth: 0.35 },
  rowAfterZ: 32,
} as const;

const ROW_TYPES = [ObstacleType.Jump, ObstacleType.LaneChange, ObstacleType.Slide] as const;
const COLLECTIBLE_SPACING = 2;
const ARC = { spacing: 1.5, overhang: 1 } as const;

const flat = () => COLLECTIBLE_FLOAT_HEIGHT;
const arcOver = (lane: number, zFrom: number, zTo: number) => {
  const center = (zFrom + zTo) / 2;
  const half = (zTo - zFrom) / 2 + ARC.overhang;
  return { lane, from: center - half, to: center + half, spacing: ARC.spacing, height: jumpArc(COLLECTIBLE_FLOAT_HEIGHT, LEVEL_CONFIG.collectibleJumpArcHeight, center, half) };
};

const { pit, blockLeft, blocksRight, beam } = PIT_LARGE;

const surfaces: GroundSurface[] = [
  { id: 'SURF_BLOCK_L', ...laneArea(WORLD_CONFIG, blockLeft.lane, blockLeft.lane, blockLeft.zFrom, blockLeft.zTo), heightFrom: 0, heightTo: 0 },
  ...blocksRight.spans.map((span, i) => ({
    id: `SURF_BLOCK_R${i + 1}`,
    ...laneArea(WORLD_CONFIG, blocksRight.lane, blocksRight.lane, span.zFrom, span.zTo),
    heightFrom: 0,
    heightTo: 0,
  })),
  {
    id: 'SURF_BEAM',
    zFrom: pit.zFrom,
    zTo: pit.zTo,
    xFrom: laneCenterX(WORLD_CONFIG, beam.lane) - beam.halfWidth,
    xTo: laneCenterX(WORLD_CONFIG, beam.lane) + beam.halfWidth,
    heightFrom: 0,
    heightTo: 0,
  },
];

/** Bottles: on the beam (risk route), arcs over the left lane's two gaps, one on each right-hand block. */
function pitCollectibles(): CollectibleSpawnPoint[] {
  const [r1, r2] = blocksRight.spans;
  const lines = [
    collectibleLine(WORLD_CONFIG, 'BEAM_L1', [{ lane: beam.lane, from: 1, to: PIT_LARGE.length - 1, spacing: COLLECTIBLE_SPACING, height: flat }]),
    collectibleLine(WORLD_CONFIG, 'PIT_ARC_L0', [
      { lane: blockLeft.lane, from: 1, to: pit.zFrom - ARC.overhang - COLLECTIBLE_SPACING, spacing: COLLECTIBLE_SPACING, height: flat },
      arcOver(blockLeft.lane, pit.zFrom, blockLeft.zFrom),
      arcOver(blockLeft.lane, blockLeft.zTo, pit.zTo),
      { lane: blockLeft.lane, from: pit.zTo + ARC.overhang + COLLECTIBLE_SPACING, to: PIT_LARGE.length - 1, spacing: COLLECTIBLE_SPACING, height: flat },
    ]),
    collectibleLine(WORLD_CONFIG, 'BLOCKS_L2', [
      { lane: blocksRight.lane, from: 1, to: pit.zFrom - 2, spacing: COLLECTIBLE_SPACING, height: flat },
      ...[r1, r2].map((span) => ({ lane: blocksRight.lane, from: (span.zFrom + span.zTo) / 2, to: (span.zFrom + span.zTo) / 2, spacing: 1, height: flat })),
      { lane: blocksRight.lane, from: pit.zTo + 2, to: PIT_LARGE.length - 1, spacing: COLLECTIBLE_SPACING, height: flat },
    ]),
  ];
  // Rounded z values of neighbouring runs may coincide – socket ids must stay unique.
  const byId = new Map<string, CollectibleSpawnPoint>();
  for (const point of lines.flat()) if (!byId.has(point.id)) byId.set(point.id, point);
  return [...byId.values()];
}

export const SEG_PIT_LARGE_40M: SegmentDefinition = {
  id: 'SEG_PIT_LARGE_40M',
  kind: SegmentKind.Straight,
  displayName: 'Baugrube groß 40 m',
  length: PIT_LARGE.length,
  width: WORLD_CONFIG.trackWidth,
  visualAssetId: 'SEG_PIT_LARGE_40M',

  entry: entryConnector(),
  exits: [straightExit(PIT_LARGE.length)],
  lanes: standardLanes(WORLD_CONFIG),

  terrain: {
    surfaces,
    gaps: [{ id: 'GAP_PIT', ...laneArea(WORLD_CONFIG, 0, WORLD_CONFIG.laneCount - 1, pit.zFrom, pit.zTo), depth: pit.depth }],
    ceilings: [],
  },

  obstacleSpawnPoints: obstacleRow(WORLD_CONFIG, 'R32', PIT_LARGE.rowAfterZ, ROW_TYPES),
  obstacleRowLayouts: { sparse: ['R32'], dense: ['R32'] },

  collectibleSpawnPoints: pitCollectibles(),

  ...moduleSides(PIT_LARGE.length),

  generation: {
    weight: 0.12,
    minDistance: 500,
    minSegmentsAfterJunction: 1,
    family: 'PIT_LARGE',
    tags: ['pit', 'terrain'],
  },
};
